import React, { useState, useEffect, useRef } from "react";
import QuestionItem from "../components/QuestionItem";
import QuestionsAnswersArray from "../components/QuestionsAnswersArray";
import { CButton, CCollapse } from "@coreui/react";
import useQuestions from "../hooks/useQuestions";
import { ReactComponent as PlusIcon } from "../assets/images/icons/plus.svg";
import { ReactComponent as BinIcon } from "../assets/images/icons/bin.svg";
import { ReactComponent as CopyIcon } from "../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../assets/images/icons/copy.svg";
import { Tooltip } from "react-tooltip";
import { FormArray, FormBuilder } from "react-reactive-form";
import useModalWindow from "../hooks/useModalWindow";
import { toast } from "react-toastify";
import {
  calculateInitialCompPdBlockIds,
  addCompPdBlock,
  removeCompPdBlock,
  duplicateCompPdBlock,
} from "./utils/pdUtils";

const ComparativePDQuestions = ({
  globalId,
  wizardForm,
  // questionsData,
  updatePdTreatmentGroupsComparisonAdditionalData,
}) => {
  const [compPdBlockIds, setCompPdBlockIds] = useState(() =>
    calculateInitialCompPdBlockIds(wizardForm)
  );
  const nextBlockIdRef = useRef(Math.max(...compPdBlockIds, 0) + 1);
  const lastBlockRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const { questionsDataSaved } = useQuestions();
  const { modalWindow } = useModalWindow();
  const [openBlockIds, setOpenBlockIds] = useState(
    new Set([compPdBlockIds[compPdBlockIds.length - 1]])
  );

  const dash = "";
  const qPdComparativeValue = "true";

  // Sync block IDs when form is restored
  useEffect(() => {
    const newIds = calculateInitialCompPdBlockIds(wizardForm);
    const currentKey = compPdBlockIds.join(",");
    const newKey = newIds.join(",");
    if (newKey !== currentKey) {
      setCompPdBlockIds(newIds);
      nextBlockIdRef.current = Math.max(...newIds, 0) + 1;
    }
  }, [wizardForm.value.qPdCompAnalyses, wizardForm.controls]);

  // Ensure controls exist for each block (using "Comp"-suffixed names)
  useEffect(() => {
    const isPdEnabled =
      wizardForm.controls.qPdCompEnabled?.value === "true" ||
      questionsDataSaved?.wizardForm?.qPdCompEnabled === "true";
    if (isPdEnabled) {
      compPdBlockIds.forEach(blockId => {
        const suffix = blockId > 1 ? `_${blockId}` : "";

        const controlsToEnsure = [
          { name: `qPdTableComp${suffix}`, defaultValue: "" },
          { name: `qPdPopulationComp${suffix}`, defaultValue: "" },
          { name: `qPdComparativeComp${suffix}`, defaultValue: "false" },
          { name: `qPdCompNameComp${suffix}`, defaultValue: "" },
          { name: `qPdSummaryTypeComp${suffix}`, defaultValue: "false" },
          { name: `qPdParametersComp${suffix}`, defaultValue: [] },
          { name: `qPdTreatmentGroupsComp${suffix}`, defaultValue: [] },
          { name: `qPdComparisonPairsComp${suffix}`, defaultValue: [] },
          { name: `qPdDoseInteractionComp${suffix}`, defaultValue: "false" },
          { name: `qPdTimepointsComp${suffix}`, defaultValue: [] },
          { name: `qPdStatTablesComp${suffix}`, defaultValue: "" },
        ];

        controlsToEnsure.forEach(({ name, defaultValue }) => {
          if (!wizardForm.contains(name)) {
            if (
              name.startsWith("qPdParametersComp") ||
              name.startsWith("qPdTreatmentGroupsComp") ||
              name.startsWith("qPdComparisonPairsComp") ||
              name.startsWith("qPdTimepointsComp")
            ) {
              wizardForm.addControl(name, new FormArray([]));
            } else {
              wizardForm.addControl(name, FormBuilder.control(defaultValue));
            }
          }
        });
      });
    }
  }, [
    wizardForm,
    compPdBlockIds,
    questionsDataSaved?.wizardForm?.qPdCompEnabled,
  ]);

  useEffect(() => {
    if (shouldScrollRef.current && lastBlockRef.current) {
      const el = lastBlockRef.current;
      let wrapper = el.parentElement;
      while (
        wrapper &&
        !wrapper.classList.contains("dashboard-content__wrapper")
      ) {
        wrapper = wrapper.parentElement;
      }
      if (wrapper) {
        const elTop = el.getBoundingClientRect().top;
        const wrapperTop = wrapper.getBoundingClientRect().top;
        wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
      }
      shouldScrollRef.current = false;
    }
  }, [compPdBlockIds.length]);

  const handleAddBlock = () => {
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    addCompPdBlock(wizardForm, newId);
    shouldScrollRef.current = true;
    setCompPdBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
  };

  const handleDuplicateBlock = (e, displayIndex) => {
    if (e) e.preventDefault();
    const sourceBlockId = compPdBlockIds[displayIndex];
    if (sourceBlockId === undefined) return;
    const newId = nextBlockIdRef.current;
    nextBlockIdRef.current += 1;
    duplicateCompPdBlock(wizardForm, sourceBlockId, newId);
    shouldScrollRef.current = true;
    setCompPdBlockIds(prev => [...prev, newId]);
    setOpenBlockIds(new Set([newId]));
    toast.success("Block duplicated successfully");
  };

  const handleDuplicateLastBlock = e => {
    if (e) e.preventDefault();
    if (compPdBlockIds.length === 0) return;
    handleDuplicateBlock(e, compPdBlockIds.length - 1);
  };

  const handleRemoveBlock = async (e, displayIndex) => {
    if (e) e.preventDefault();
    if (compPdBlockIds.length <= 1) return;
    const confirmed = await modalWindow({
      title: "Remove Block",
      message: "Do you want to remove this block of questions?",
      confirmButtonLabel: "Yes",
    });
    if (!confirmed) return;
    const blockId = compPdBlockIds[displayIndex];
    removeCompPdBlock(wizardForm, blockId);
    const remainingIds = compPdBlockIds.filter((_, i) => i !== displayIndex);
    setCompPdBlockIds(remainingIds);
    setOpenBlockIds(new Set([remainingIds[remainingIds.length - 1]]));
  };

  const renderBlockContent = (blockId, displayIndex) => {
    const suffix = blockId > 1 ? `_${blockId}` : "";
    const isFirstBlock = displayIndex === 0;
    const blockNumber = displayIndex + 1;

    const q = n => (isFirstBlock ? `${n}` : `${n}${dash}(${blockNumber})`);
    const questionIndex1a = q(2);
    const questionIndex1b = q(3);
    // const questionIndex1c = q(4);
    const questionIndex1ci = q(4);
    const questionIndex1cii = q(5);
    const questionIndex1ciii = q(6);
    const questionIndex1civ = q(7);
    const questionIndex1cv = q(8);
    const questionIndex1cvi = q(9);
    const questionIndex1d = q(10);

    const qPdComparativeValueWithoutStatistics =
      qPdComparativeValue &&
      wizardForm.controls[`qPdSummaryTypeComp${suffix}`]?.value === "false";

    const qPdComparativeValueWithStatistics =
      qPdComparativeValue &&
      wizardForm.controls[`qPdSummaryTypeComp${suffix}`]?.value === "true";

    return (
      <>
        {wizardForm.controls.qPdCompEnabled?.value === "true" && (
          <div
            style={{
              marginBottom: "1rem",
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
            }}>
            <CButton
              color={"primary"}
              shape={"pill"}
              data-tooltip-id={`duplicate-comp-pd-block-${blockId}`}
              style={{
                border: "1px solid #006dee",
                background: "#006dee",
                fontSize: "15px",
                color: "#fff",
                boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
              }}
              onClick={e => handleDuplicateBlock(e, displayIndex)}>
              <CopyIcon /> Duplicate
            </CButton>
            <Tooltip
              id={`duplicate-comp-pd-block-${blockId}`}
              place="top"
              delayShow={100}
              className="custom-tooltip">
              Duplicate this block
            </Tooltip>
            {!isFirstBlock && (
              <>
                <CButton
                  color={"danger"}
                  shape={"pill"}
                  data-tooltip-id={`remove-comp-pd-block-${blockId}`}
                  onClick={e => handleRemoveBlock(e, displayIndex)}>
                  <BinIcon />
                </CButton>
                <Tooltip
                  id={`remove-comp-pd-block-${blockId}`}
                  place="top"
                  delayShow={100}
                  className="custom-tooltip">
                  Remove this block
                </Tooltip>
              </>
            )}
          </div>
        )}

        <QuestionItem
          questionIndex={questionIndex1a}
          questionName={`qPdTableComp${suffix}`}
          questionsName={"qPdTable"}
          suffix={suffix}
          activator={wizardForm.controls.qPdCompEnabled?.value === "true"}
          formGroup={wizardForm}
          isPlainSingleSelectDropdown={true}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1b}
          questionName={`qPdPopulationComp${suffix}`}
          questionsName={"qPdPopulation"}
          suffix={suffix}
          activator={wizardForm.controls.qPdCompEnabled?.value === "true"}
          questionInputType={"text"}
          globalId={globalId}
        />
        {/* <QuestionItem
          questionIndex={questionIndex1c}
          questionName={`qPdComparativeComp${suffix}`}
          questionsName={"qPdComparative"}
          suffix={suffix}
          activator={wizardForm.controls.qPdCompEnabled?.value === "true"}
          questionInputType={"radio"}
          questionInputChoice={"qPdComparative"}
          globalId={globalId}
        /> */}
        <QuestionItem
          questionIndex={questionIndex1ci}
          questionName={`qPdCompNameComp${suffix}`}
          questionsName={"qPdCompName"}
          suffix={suffix}
          activator={true}
          questionInputType={"text"}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1cii}
          questionName={`qPdSummaryTypeComp${suffix}`}
          questionsName={"qPdSummaryType"}
          suffix={suffix}
          activator={qPdComparativeValue}
          questionInputType={"radio"}
          questionInputChoice={"yes_no"}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1ciii}
          controlName={`qPdParametersComp${suffix}`}
          questionsName={"qPdParameters"}
          activator={qPdComparativeValueWithStatistics}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "text"]]}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1ciii}
          controlName={`qPdTreatmentGroupsComp${suffix}`}
          questionsName={"qPdTreatmentGroups"}
          activator={qPdComparativeValueWithoutStatistics}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "richtext"]]}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1civ}
          controlName={`qPdComparisonPairsComp${suffix}`}
          questionsName={"qPdComparisonPairs"}
          activator={qPdComparativeValueWithoutStatistics}
          formGroup={wizardForm}
          inputTextBoxes={[
            ["Treatment", "Treatment", "select"],
            ["Comparator", "Comparator", "select"],
          ]}
          inputTextBoxesAdditionalData={() =>
            updatePdTreatmentGroupsComparisonAdditionalData(suffix)
          }
        />
        <QuestionItem
          questionIndex={questionIndex1cv}
          questionName={`qPdDoseInteractionComp${suffix}`}
          questionsName={"qPdDoseInteraction"}
          suffix={suffix}
          activator={qPdComparativeValueWithoutStatistics}
          questionInputType={"radio"}
          questionInputChoice={"qPdDoseInteraction"}
          globalId={globalId}
        />
        <QuestionsAnswersArray
          questionIndex={questionIndex1cvi}
          controlName={`qPdTimepointsComp${suffix}`}
          questionsName={"qPdTimepoints"}
          activator={qPdComparativeValueWithoutStatistics}
          formGroup={wizardForm}
          inputTextBoxes={[["", "valueHelper", "text"]]}
          globalId={globalId}
        />
        <QuestionItem
          questionIndex={questionIndex1d}
          questionName={`qPdStatTablesComp${suffix}`}
          questionsName={"qPdStatTables"}
          suffix={suffix}
          activator={qPdComparativeValueWithoutStatistics}
          formGroup={wizardForm}
          isPlainMultiSelectDropdown={true}
          tables={true}
          globalId={globalId}
        />
      </>
    );
  };

  return (
    <>
      <QuestionItem
        questionIndex={"1"}
        questionName={"qPdCompEnabled"}
        activator={"true"}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />

      {wizardForm.controls.qPdCompEnabled?.value === "true" && (
        <>
          {compPdBlockIds.length === 1 ? (
            <div
              ref={lastBlockRef}
              className="analysis-block"
              //style={{ marginBottom: "2rem" }}
            >
              {renderBlockContent(compPdBlockIds[0], 0)}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "0.5rem",
                  marginBottom: "0.5rem",
                }}>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set(compPdBlockIds))}>
                  Expand all
                </CButton>
                <CButton
                  color={"primary"}
                  shape={"pill"}
                  style={{
                    border: "1px solid #006dee",
                    background: "#006dee",
                    fontSize: "15px",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0, 109, 238, 0.25)",
                  }}
                  onClick={() => setOpenBlockIds(new Set())}>
                  Collapse all
                </CButton>
              </div>
              <div className="accordion">
                {compPdBlockIds.map((blockId, displayIndex) => {
                  const isOpen = openBlockIds.has(blockId);
                  return (
                    <div
                      key={`pd-comp-block-${blockId}`}
                      className="accordion-item"
                      ref={
                        displayIndex === compPdBlockIds.length - 1
                          ? lastBlockRef
                          : null
                      }>
                      <div className="accordion-header">
                        <button
                          type="button"
                          className={`accordion-button${isOpen ? "" : " collapsed"}`}
                          onClick={() =>
                            setOpenBlockIds(prev => {
                              const next = new Set(prev);
                              next.has(blockId)
                                ? next.delete(blockId)
                                : next.add(blockId);
                              return next;
                            })
                          }>
                          Block {displayIndex + 1}
                        </button>
                      </div>
                      <CCollapse
                        visible={isOpen}
                        className="accordion-collapse">
                        <div className="accordion-body">
                          <div
                            className="analysis-block"
                            //style={{ marginBottom: "2rem" }}
                          >
                            {renderBlockContent(blockId, displayIndex)}
                          </div>
                        </div>
                      </CCollapse>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {wizardForm.controls.qPdCompEnabled?.value === "true" && (
            <div
              style={{
                marginTop: "1rem",
                marginBottom: "2rem",
                display: "flex",
                gap: "0.5rem",
              }}>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleAddBlock}>
                Add another Comparative PD analysis <PlusIcon />
              </CButton>
              <CButton
                color={"primary"}
                variant={"outline"}
                className={"btn-light"}
                onClick={handleDuplicateLastBlock}>
                Duplicate block above <CopyOutlineIcon />
              </CButton>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ComparativePDQuestions;
